import React from "react";
import toast from "react-hot-toast";
import styled, { useTheme, keyframes } from "styled-components";
import { FileText, Sparkles } from "lucide-react";
import CallChatNotification from "../Components/CallChatComponents/CallChatNotification";
import { ReactComponent as ChatIcon } from "../Assets/iconsv2/controlButtons/ChatsActive.svg";
import { ReactComponent as PollIcon } from "../Assets/iconsv2/controlButtons/PollBarsActive.svg";
import { ReactComponent as RecordCameraIcon } from "../Assets/iconsv2/controlButtons/RecordCamera.svg";

const MEETING_NOTICE_DURATION_MS = 4000;
const MEETING_NOTICE_GAP_MS = 400;
let nextMeetingNoticeAt = 0;
let meetingNoticeTimers = [];
let meetingNoticeToastIds = [];

export const cancelMeetingNotices = () => {
  meetingNoticeTimers.forEach(clearTimeout);
  meetingNoticeToastIds.forEach((id) => toast.dismiss(id));
  meetingNoticeTimers = [];
  meetingNoticeToastIds = [];
  nextMeetingNoticeAt = 0;
};

export const useNotify = () => {
  const theme = useTheme(); // Get the theme from styled-components

  const notifySuccess = (message) => {
    toast.success(message, {
      style: {
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "20px",
        borderRadius: "4px",
        color: theme.menu.primaryTextColor,
        border: `1px solid ${theme.menu.selectedTextColor}`,
        background: theme.menu.background,
      },
    });
  };

  const notifyError = (message) => {
    toast.error(message, {
      style: {
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "20px",
        borderRadius: "4px",
        color: theme.menu.primaryTextColor,
        border: `1px solid ${theme.menu.deleteColor}`,
        background: theme.menu.background,
      },
    });
  };

  const notifyInfo = (message) => {
    toast(message, {
      style: {
        fontSize: "16px",
        fontWeight: "500",
        lineHeight: "20px",
        borderRadius: "4px",
        color: theme.menu.primaryTextColor,
        border: `1px solid ${theme.menu.primaryTextColor}`,
        background: theme.menu.background,
      },
    });
  };

  const notifyCallChatMessage = (senderName, messagePreview, onClickAction, senderAvatar = null) => {
    toast.custom(
      (t) => (
        <CallChatNotification
          icon={<ChatIcon width={18} height={18} />}
          title={senderName}
          message={messagePreview}
          avatar={senderAvatar}
          onClick={onClickAction}
          onClose={() => toast.dismiss(t.id)}
        />
      ),
      {
        duration: 5000,
        position: "top-center",
        style: {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
        },
      }
    );
  };

  const notifyCallPoll = (pollQuestion, onClickAction) => {
    toast.custom(
      (t) => (
        <CallChatNotification
          icon={<PollIcon width={18} height={18} />}
          title="New Poll"
          message={pollQuestion}
          avatarFallback=""
          accentColor="#FF3B30"
          indicatorColor="#FF3B30"
          onClick={onClickAction}
          onClose={() => toast.dismiss(t.id)}
        />
      ),
      {
        duration: 5000,
        position: "top-center",
        style: {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
        },
      }
    );
  };

  const showMeetingFeatureStarted = (featureName, userName, icon) => {
    const toastId = toast.custom(
      (t) => (
        <RecordingToastOverlay>
          <RecordingToastContent $theme={theme}>
            <RecordingToastIcon $theme={theme}>{icon}</RecordingToastIcon>
            <RecordingToastBody>
              <RecordingToastText $theme={theme}>
                Meeting {featureName} started{userName && <> by <strong>{userName}</strong></>}.{" "}
                Read more about the{" "}
                <RecordingToastLink
                  href="https://emotii.ai/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  $theme={theme}
                >
                  Privacy Policy
                </RecordingToastLink>
                .
              </RecordingToastText>
            </RecordingToastBody>
            <RecordingToastClose
              onClick={() => toast.dismiss(t.id)}
              $theme={theme}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </RecordingToastClose>
          </RecordingToastContent>
        </RecordingToastOverlay>
      ),
      {
        duration: MEETING_NOTICE_DURATION_MS,
        style: {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          maxWidth: "none",
        },
      }
    );
    meetingNoticeToastIds.push(toastId);
  };

  const notifyMeetingFeatureStarted = (featureName, userName, icon) => {
    const now = Date.now();
    const showAt = Math.max(now, nextMeetingNoticeAt);
    nextMeetingNoticeAt = showAt + MEETING_NOTICE_DURATION_MS + MEETING_NOTICE_GAP_MS;
    const timer = setTimeout(() => {
      meetingNoticeTimers = meetingNoticeTimers.filter((pending) => pending !== timer);
      showMeetingFeatureStarted(featureName, userName, icon);
    }, showAt - now);
    meetingNoticeTimers.push(timer);
  };

  const notifyRecordingStarted = (userName) =>
    notifyMeetingFeatureStarted("Recording", userName, <RecordCameraIcon width={20} height={20} />);

  const notifyTranscriptionStarted = (userName) =>
    notifyMeetingFeatureStarted("Transcription", userName, <FileText size={20} />);

  const notifyAiSummaryStarted = (userName) =>
    notifyMeetingFeatureStarted("AI Summary", userName, <Sparkles size={20} />);

  return { notifySuccess, notifyError, notifyInfo, notifyCallChatMessage, notifyCallPoll, notifyRecordingStarted, notifyTranscriptionStarted, notifyAiSummaryStarted };
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const RecordingToastOverlay = styled.div`
  animation: ${fadeIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const RecordingToastContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  background: ${({ $theme }) => $theme.callComponent.participantsListBackground};
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 16px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 0 1px ${({ $theme }) => $theme.sidebar.borderColor} inset;
  backdrop-filter: blur(20px);
  min-width: 380px;
  max-width: 480px;
  position: relative;
`;

const RecordingToastIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  flex-shrink: 0;

  svg {
    color: #ef4444;
    stroke: #ef4444;
  }
`;

const RecordingToastBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RecordingToastText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ $theme }) => $theme.menu.primaryTextColor};
  margin: 0;

  strong {
    font-weight: 600;
  }
`;

const RecordingToastLink = styled.a`
  color: ${({ $theme }) => $theme.menu.selectedTextColor};
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`;

const RecordingToastClose = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
`;
